import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import { IAppointmentReportResponse } from "@/models/ApiRequestResponse/report";

import { getInnerScheduleReportApi } from "@/utils/apis/doctor/report";

import { RootState } from "@/store/store";

interface InnerScheduleReportState {
  data: IAppointmentReportResponse | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

export const fetchInnerScheduleReport = createAsyncThunk<
  IAppointmentReportResponse,
  string
>("innerScheduleReport/fetch", async (scheduleId) => {
  const response = await getInnerScheduleReportApi(scheduleId);

  if ("result" in response) {
    return response;
  }

  throw new Error("Failed to fetch inner schedule report");
});

const initialState: InnerScheduleReportState = {
  data: null,
  status: "idle",
  error: null,
};

const innerScheduleReportSlice = createSlice({
  name: "innerScheduleReport",
  initialState,
  reducers: {
    resetReport: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInnerScheduleReport.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchInnerScheduleReport.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.data = action.payload;
      })
      .addCase(fetchInnerScheduleReport.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.error.message || "Failed to fetch inner schedule report";
      });
  },
});

const selectInnerScheduleReport = (state: RootState) => state.innerSchedule;

export const innerScheduleReportSelector = createSelector(
  [selectInnerScheduleReport],
  (innerScheduleReport) => ({
    data: innerScheduleReport.data,
    status: innerScheduleReport.status,
    error: innerScheduleReport.error,
  })
);

export const { resetReport } = innerScheduleReportSlice.actions;
export default innerScheduleReportSlice.reducer;
